import {Skeleton} from '@components/ui/skeleton'
import {useTranslation} from '@/translations/i18n.tsx'
import {FreelancerPaymentDate} from '@features/payments/types.ts'
import {ChevronUpIcon, ChevronDownIcon} from '@components/ui/icon'
import {Button} from '@components/ui/button'
import {useState} from 'react'
import {formatPaymentAmount} from '@features/payments/utils.ts'
import {PaymentsAccordionBody} from '@features/payments/components/payments-accordion-body.tsx'
import {formatLocaleDate} from '@/dates.ts'
import {raise} from '@utilities/helpers.ts'
import {ButtonLink} from '@components/commons/button-link'
import {UPWORK_URL} from '../constants'

type PaymentsAccordionProps =
    | {
          isLoading?: false
          paymentDate: FreelancerPaymentDate['data'][number]
      }
    | {
          isLoading: true
          paymentDate?: undefined
      }

export const PaymentsAccordion = ({isLoading, paymentDate}: PaymentsAccordionProps) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)

    return (
        <div className="border-b border-neutral-300">
            <div className="flex items-center justify-between py-3 px-2 sm:px-5">
                <hgroup className="flex flex-col gap-1">
                    <h3 className="text-sm font-medium text-neutral-900 sm:text-md">
                        {isLoading ? (
                            <Skeleton height={20} width={160} />
                        ) : (
                            formatLocaleDate(paymentDate.payment_date, 'PP')
                        )}
                    </h3>

                    <h5 className="text-xs font-normal text-neutral-500 sm:text-sm">
                        {isLoading ? (
                            <Skeleton height={16} width={120} />
                        ) : (
                            t('transactions:transactions', {count: paymentDate.payment_count})
                        )}
                    </h5>
                </hgroup>
                {isLoading ? (
                    <Skeleton height={24} width={200} />
                ) : paymentDate?.is_paid_via_upwork ? (
                    <ButtonLink to={UPWORK_URL} target="_blank" rel="noopener noreferrer" variant="secondary" size="sm">
                        {t('assignment:go_to_upwork_cta')}
                    </ButtonLink>
                ) : (
                    <Button size="sm" variant="tertiary" onClick={() => setIsOpen(!isOpen)}>
                        {formatPaymentAmount({
                            amount: paymentDate.paid_out_photographer_currency_amount,
                            amountUsd: paymentDate.paid_out,
                            currency: paymentDate.paid_out_photographer_currency
                        })}
                        {isOpen ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
                    </Button>
                )}
            </div>
            {isOpen && (
                <PaymentsAccordionBody paymentDate={paymentDate?.payment_date ?? raise('payment_date is undefined')} />
            )}
        </div>
    )
}
